import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, AlertCircle, CheckCircle, Search, X, ChevronDown, Calendar, Sparkles, ArrowRight, Layers, LogIn, MapPin, Briefcase, Globe, RotateCcw, SlidersHorizontal, LayoutGrid, List, Zap, Building2, Laptop, ChevronLeft, ChevronRight, Menu } from 'lucide-react';
import { usePersistentFilters } from '../../hooks/usePersistentFilters';
import BASE_URL from '../../url';
import '../styles/JobPortal.css';
import prophecyLogo2 from "../../Recruitment/Assets/images/prophecy-logo2.png";
import prophecyLogo from "../../Recruitment/Assets/images/prophecy-logo.png";
import careersHeroBg from "../../Recruitment/Assets/images/careers-hero-bg.png";
import { countriesData, stateMapping } from '../config/locationConfig';
import PublicBenchCandidateModal from './PublicBenchCandidateModal';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const VISA_TYPES = ['H1B', 'H4', 'L1', 'L2', 'O1', 'EB1', 'EB2', 'EB3', 'GC', 'EAD', 'USC', 'GC Holder', 'TN', 'E2'];

// Helper function to format date display
const formatDateDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).replace(/,/g, '');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

// Canonical Country normalizer (Canada, India, USA)
const normalizeCountry = (c) => {
  if (!c) return '';
  const clean = c.toString().trim().toUpperCase();
  if (clean === 'CA' || clean === 'CANADA') return 'Canada';
  if (clean === 'US' || clean === 'USA' || clean === 'UNITED STATES') return 'USA';
  if (clean === 'IN' || clean === 'INDIA') return 'India';
  return c.toString().trim();
};



// Canonical Location Aliases & Typo Normalizer
const CANONICAL_LOCATION_ALIASES = {
  'COLOMBUS, OH, USA': 'Columbus, OH, USA',
  'COLOMBUS, OH': 'Columbus, OH, USA',
  'COLOMBUS, OH, US': 'Columbus, OH, USA',
  'CHAROLOTTE, NC, USA': 'Charlotte, NC, USA',
  'CHAROLOTTE, NC': 'Charlotte, NC, USA',
  'CHAROLOTTE, NC, US': 'Charlotte, NC, USA',
  'HACKENSACK, NEW, USA': 'Hackensack, NJ, USA',
  'HACKENSACK, NEW, US': 'Hackensack, NJ, USA',
  'HACKENSACK, NEW JERSEY, USA': 'Hackensack, NJ, USA',
  'HACKENSACK, NEW JERSEY, US': 'Hackensack, NJ, USA',
  'NEW YOURK CITY, NEW YORK, USA': 'New York, NY, USA',
  'NEW YOURK CITY, NY, USA': 'New York, NY, USA',
  'NEW YOURK CITY, NEW YORK, US': 'New York, NY, USA',
  'NEW YORK CITY, NY, USA': 'New York, NY, USA',
  'NEW YORK CITY, NY, US': 'New York, NY, USA',
  'HYBRID DALLAS, TX, USA': 'Dallas, TX, USA',
  'HYBRID DALLAS, TX, US': 'Dallas, TX, USA',
  'ATLANTA, REMOTE,GA, USA': 'Atlanta, GA, USA',
  'ATLANTA, REMOTE, GA, USA': 'Atlanta, GA, USA',
  'CARY, NEW JERSEY, NC, NJ, USA': 'Cary, NC, USA',
  'BHUVANESWAR, MP, INDIA': 'Bhubaneswar, OD, India',
  'BHUBANESWAR, OD, IN': 'Bhubaneswar, OD, India',
  'KOCHIN, KL, INDIA': 'Kochi, KL, India',
  'KOCHIN, KL, IN': 'Kochi, KL, India',
};

const TYPO_CITY_MAP = {
  'COLOMBUS': 'Columbus',
  'CHAROLOTTE': 'Charlotte',
  'NEW YOURK CITY': 'New York',
  'NEW YORK CITY': 'New York',
  'HYBRID DALLAS': 'Dallas',
  'BHUVANESWAR': 'Bhubaneswar',
  'KOCHIN': 'Kochi'
};

const STATE_NAME_TO_CODE = {
  'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
  'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
  'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
  'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
  'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS',
  'MISSOURI': 'MO', 'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ', 'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND', 'OHIO': 'OH', 'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA',
  'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC', 'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN',
  'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT', 'VIRGINIA': 'VA', 'WASHINGTON': 'WA',
  'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY', 'NEW': 'NJ'
};

// Helper function to sanitize and canonicalize a single location string
export const canonicalizeLocation = (loc) => {
  if (!loc || loc === 'N/A' || loc === 'Not specified') return null;

  // Clean extra spaces, leading/trailing dashes and commas
  let cleaned = loc
    .trim()
    .replace(/^[\-,\s]+|[\-,\s]+$/g, '')
    .replace(/\s*,\s*/g, ', ')
    .replace(/,(\s*,)+/g, ', ')
    .trim();

  if (!cleaned || /^[\-,\s]+$/.test(cleaned) || cleaned === 'Remote' || cleaned === 'N/A') {
    return null;
  }

  const upper = cleaned.toUpperCase();
  if (CANONICAL_LOCATION_ALIASES[upper]) {
    return CANONICAL_LOCATION_ALIASES[upper];
  }

  const parts = cleaned.split(',').map(p => p.trim()).filter(p => p && p !== '-');
  if (parts.length === 0) return null;

  if (parts.length >= 3) {
    let country = normalizeCountry(parts[parts.length - 1]);
    let state = parts[parts.length - 2];
    let city = parts.slice(0, parts.length - 2).join(', ');

    // Normalize State if full name or known typo
    const stateUpper = state.toUpperCase();
    if (STATE_NAME_TO_CODE[stateUpper]) {
      state = STATE_NAME_TO_CODE[stateUpper];
    }

    // Normalize City typo
    const cityUpper = city.toUpperCase();
    if (TYPO_CITY_MAP[cityUpper]) {
      city = TYPO_CITY_MAP[cityUpper];
    } else if (cityUpper.startsWith('HYBRID ')) {
      city = city.replace(/^HYBRID\s+/i, '');
    }

    // If city is empty or invalid
    if (!city || city === '-' || city === 'Statewide') {
      return state ? `Statewide, ${state}, ${country}` : `Statewide, ${country}`;
    }

    // Format city casing
    const formattedCity = city.replace(/\b\w/g, char => char.toUpperCase());
    return `${formattedCity}, ${state.toUpperCase()}, ${country}`;
  }

  if (parts.length === 2) {
    const p1 = parts[0];
    const p2 = parts[1];
    const p2Country = normalizeCountry(p2);
    if (p2Country === 'USA' || p2Country === 'Canada' || p2Country === 'India') {
      const p1Upper = p1.toUpperCase();
      if (STATE_NAME_TO_CODE[p1Upper]) {
        return `Statewide, ${STATE_NAME_TO_CODE[p1Upper]}, ${p2Country}`;
      }
      return `${p1.replace(/\b\w/g, char => char.toUpperCase())}, ${p2Country}`;
    }
    const stateUpper = p2.toUpperCase();
    const stateCode = STATE_NAME_TO_CODE[stateUpper] || stateUpper;
    return `${p1.replace(/\b\w/g, char => char.toUpperCase())}, ${stateCode}, USA`;
  }

  return cleaned.replace(/\b\w/g, char => char.toUpperCase());
};

// Helper function to format locations
const normalizeLocationString = (loc) => {
  if (!loc || loc === 'N/A') return 'N/A';
  const canonical = canonicalizeLocation(loc);
  return canonical || 'N/A';
};

// Helper function to format locations
const formatLocations = (role) => {
  try {
    let result = [];
    if (role.locations && Array.isArray(role.locations)) {
      result = role.locations
        .filter(loc => loc && loc.trim() !== '');
    } else if (role.location && typeof role.location === 'string') {
      const locationStr = role.location.trim();
      
      if (!locationStr || locationStr === 'N/A') {
        result = ['N/A'];
      } else if (locationStr.includes('|')) {
        result = locationStr
          .split('|')
          .filter(loc => loc && loc.trim() !== '');
      } else {
        const parts = locationStr.split(',').map(p => p.trim());
        let parsed = false;
        
        if (parts.length >= 3) {
          const country = normalizeCountry(parts[parts.length - 1]);
          const citiesAndStates = parts.slice(0, parts.length - 1);
          
          let stateCount = 0;
          for (let i = citiesAndStates.length - 1; i >= 0; i--) {
            if (/^[A-Z]{2}$/i.test(citiesAndStates[i])) {
              stateCount++;
            } else {
              break;
            }
          }
          
          const cityCount = citiesAndStates.length - stateCount;
          
          if (cityCount > 0 && stateCount > 0) {
            let isValidPattern = true;
            for (let i = 0; i < cityCount; i++) {
              if (/^[A-Z]{2}$/i.test(citiesAndStates[i])) {
                isValidPattern = false;
                break;
              }
            }
            
            if (isValidPattern) {
              const cities = citiesAndStates.slice(0, cityCount);
              const states = citiesAndStates.slice(cityCount);
              const locations = [];
              
              for (let i = 0; i < cities.length; i++) {
                const state = states[i] || states[states.length - 1];
                locations.push(`${cities[i]}, ${state}, ${country}`);
              }
              
              if (locations.length > 0) {
                result = locations;
                parsed = true;
              }
            }
          }
        }
        
        if (!parsed) {
          result = [parts.map((p, idx) => idx === parts.length - 1 && parts.length >= 2 ? normalizeCountry(p) : p).join(', ')];
        }
      }
    } else {
      result = ['N/A'];
    }
    
    const formatted = result
      .map(normalizeLocationString)
      .filter(l => l && l !== 'N/A');

    return formatted.length > 0 ? formatted : ['Not specified'];

  } catch (error) {
    console.error('Error formatting locations for role:', role.role, error);
    return ['Not specified'];
  }
};

// Auth Required Modal
const AuthRequiredModal = ({ isOpen, onClose, onLoginClick, onGuestClick }) => {
  if (!isOpen) return null;

  return (
    <div className="jp-modal-backdrop">
      <div className="jp-modal-box jp-auth-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="jp-modal-header">
          <h2 className="jp-modal-header-text">Sign In Required</h2>
          <button className="jp-modal-close-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="jp-modal-body jp-auth-modal-body">
          <p className="jp-auth-message">
            Please sign in or create an account to track your applications, or continue as a guest.
          </p>
          
          <div className="jp-auth-button-group">
            <button className="jp-auth-login-btn" onClick={onLoginClick}>
              Login / Signup
            </button>
            <button className="jp-auth-guest-btn" onClick={onGuestClick}>
              Apply as Guest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Job Details Modal
const JobDetailsModal = ({ isOpen, onClose, job, onApplyClick }) => {
  if (!isOpen || !job) return null;

  return (
    <div className="jp-modal-backdrop jp-modal-backdrop-details">
      <div className="jp-modal-box jp-modal-box-details">
        <div className="jp-modal-header">
          <h2 className="jp-modal-header-text">{job.role}</h2>
          <button className="jp-modal-close-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="jp-modal-body">
          <div className="jp-job-detail">
            <h3 className="jp-job-detail-label">Role Type</h3>
            <p className="jp-job-detail-content">{job.roleType || 'N/A'}</p>
          </div>

          <div className="jp-job-detail">
            <h3 className="jp-job-detail-label">Location</h3>
            <p className="jp-job-detail-content">
              {formatLocations(job).join(', ') || 'N/A'}
            </p>
          </div>

          <div className="jp-job-detail">
            <h3 className="jp-job-detail-label">Work Mode</h3>
            <p className="jp-job-detail-content">{job.roleLocation || 'Onsite'}</p>
          </div>

          <div className="jp-job-detail">
            <h3 className="jp-job-detail-label">Experience</h3>
            <p className="jp-job-detail-content">{job.experience || 'N/A'}</p>
          </div>

          <div className="jp-job-detail">
            <h3 className="jp-job-detail-label">Posted Date</h3>
            <p className="jp-job-detail-content">
              {formatDateDisplay(job.postedDate) || 'N/A'}
            </p>
          </div>

          <div className="jp-job-detail">
            <h3 className="jp-job-detail-label">Description</h3>
            <div className="jp-job-desc">
              {(() => {
                const desc = job.description || job.jobDescription || 'No description available';
                
                const cleanDesc = desc
                  .replace(/<[^>]*>/g, '')
                  .replace(/&nbsp;/g, ' ')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&amp;/g, '&')
                  .trim();

                const sections = cleanDesc.split(/(?=(?:position|experience|location|employment type|about the role|key responsibilities|required skills|preferred|additional skills|educational qualification|soft skills)[\s:]*)/i);

                return sections.map((section, idx) => {
                  if (!section.trim()) return null;

                  const lines = section.split('\n').map(l => l.trim()).filter(l => l);
                  if (lines.length === 0) return null;

                  const firstLine = lines[0];
                  const isHeader = /^(position|experience|location|employment type|about the role|key responsibilities|required skills|preferred|additional skills|educational qualification|soft skills)[\s:]*$/i.test(firstLine);

                  if (isHeader) {
                    const headerText = firstLine.replace(/[\s:]*$/i, '');
                    const contentLines = lines.slice(1);

                    return (
                      <div key={idx} className="jp-job-desc-section">
                        <h4 className="jp-job-desc-heading">{headerText}</h4>
                        <div className="jp-job-desc-list">
                          {contentLines.map((line, lineIdx) => {
                            const trimmed = line.trim();
                            if (!trimmed) return null;

                            if (trimmed.match(/^[\*•\-]/)) {
                              return (
                                <div key={lineIdx} className="jp-job-desc-item">
                                  <span className="jp-job-desc-bullet">•</span>
                                  <span>{trimmed.replace(/^[\*•\-]\s*/, '')}</span>
                                </div>
                              );
                            }

                            return (
                              <p key={lineIdx} className="jp-job-desc-paragraph">
                                {trimmed}
                              </p>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  if (firstLine.match(/^[\*•\-]/)) {
                    return (
                      <div key={idx} className="jp-job-desc-item">
                        <span className="jp-job-desc-bullet">•</span>
                        <span>{firstLine.replace(/^[\*•\-]\s*/, '')}</span>
                      </div>
                    );
                  }

                  return (
                    <p key={idx} className="jp-job-desc-paragraph">
                      {lines.join(' ')}
                    </p>
                  );
                }).filter(Boolean);
              })()}
            </div>
          </div>

          <button
            onClick={() => {
              onApplyClick(job);
              onClose();
            }}
            className="jp-apply-button jp-details-apply-button"
          >
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
};

// Candidate Form Popup
const CandidateFormPopup = ({ isOpen, onClose, selectedRole }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
    visa_type: '',
    current_state: '',
    current_city: '',
    country: '',
    employment_type: [],
    job_id: ''
  });

  const [resumeFile, setResumeFile] = useState(null);
  const statesList = formData.country ? Object.keys(stateMapping[formData.country] || {}) : [];
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleRedirectToJoinBench = () => {
    onClose();
    const benchState = {
      FirstName: formData.first_name || '',
      LastName: formData.last_name || '',
      Email: formData.email || '',
      Phone: formData.phone || '',
      Mobile: formData.phone || '',
      JobTitle: formData.position || '',
      WorkAuthorization: formData.visa_type || '',
      LocationCountry: formData.country || 'US',
      LocationState: formData.current_state || '',
      LocationCity: formData.current_city || '',
      EmploymentType: (formData.employment_type && formData.employment_type.length > 0) ? formData.employment_type.join(', ') : '',
      ResumeFile: resumeFile || null
    };
    if (navigate) {
      navigate('/join-our-bench', { state: benchState });
    } else {
      window.location.href = '/join-our-bench';
    }
  };

  useEffect(() => {
    if (isOpen) {
      const pendingApplication = localStorage.getItem('pendingJobApplication');
      if (pendingApplication) {
        const role = JSON.parse(pendingApplication);
        setFormData(prev => ({
          ...prev,
          position: role.role,
          job_id: role.id || role.jobId || role.job_id || ''
        }));
        localStorage.removeItem('pendingJobApplication');
      } else if (selectedRole && selectedRole.role) {
        setFormData(prev => ({
          ...prev,
          position: selectedRole.role,
          job_id: selectedRole.jobId || selectedRole.job_id || selectedRole.id || selectedRole.systemId || ''
        }));
      }
    }
  }, [isOpen, selectedRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      setFormData(prev => ({
        ...prev,
        country: value,
        current_state: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    setError('');
  };

  const handleEmploymentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      employment_type: prev.employment_type.includes(type)
        ? prev.employment_type.filter(t => t !== type)
        : [...prev.employment_type, type]
    }));
  };

  const handleFileUpload = (file) => {
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const validExtensions = ['.pdf', '.doc', '.docx'];
    
    const fileName = file.name.toLowerCase();
    const fileType = file.type;
    const ext = fileName.substring(fileName.lastIndexOf('.'));

    if (!validTypes.includes(fileType) && !validExtensions.includes(ext)) {
      setError('Invalid file type. Please upload PDF, DOC, or DOCX files only.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File size exceeds 5MB limit.');
      return;
    }

    setResumeFile(file);
    setError('');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files?.[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files?.[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const [showBenchModal, setShowBenchModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.first_name.trim()) { setError('First name is required'); return; }
    if (!formData.last_name.trim()) { setError('Last name is required'); return; }
    if (!formData.email.trim()) { setError('Email is required'); return; }
    if (!formData.phone.trim()) { setError('Phone number is required'); return; }
    if (!resumeFile) { setError('Resume file is required'); return; }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.first_name);
      formDataToSend.append('last_name', formData.last_name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('visa_type', formData.visa_type || '');
      formDataToSend.append('current_state', formData.current_state || '');
      formDataToSend.append('current_city', formData.current_city || '');
      formDataToSend.append('country', formData.country || '');
      formDataToSend.append('employment_type', formData.employment_type.join(',') || '');
      formDataToSend.append('job_id', formData.job_id || '');
      formDataToSend.append('resume', resumeFile);

      const response = await fetch(`${BASE_URL}/api/ats/apply`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to submit application');
        return;
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {showBenchModal && (
        <PublicBenchCandidateModal
          isOpen={showBenchModal}
          onClose={() => {
            setShowBenchModal(false);
            onClose();
          }}
          initialData={{
            ...formData,
            resumeFile
          }}
        />
      )}

      {!showBenchModal && (
        <div className="jp-modal-backdrop">
          <div className="jp-modal-box">
            <div className="jp-modal-header">
              <h2 className="jp-modal-header-text">Apply for Position</h2>
              <button className="jp-modal-close-icon" onClick={onClose}><X size={18} /></button>
            </div>
            <div className="jp-modal-body">
              <form onSubmit={handleSubmit}>
                {error && (
                  <div className="jp-alert jp-alert-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                {submitted ? (
                  <div style={{ textAlign: 'center', padding: '16px 8px 8px' }}>
                    <div className="jp-alert jp-alert-success" style={{ justifyContent: 'center', marginBottom: '24px' }}>
                      <CheckCircle size={20} />
                      <span style={{ fontWeight: 600 }}>Application submitted successfully!</span>
                    </div>

                    {/* Bench Enrollment Prompt Box */}
                    <div style={{
                      background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)',
                      border: '1.5px solid #5eead4',
                      borderRadius: '14px',
                      padding: '24px',
                      textAlign: 'left',
                      boxShadow: '0 4px 6px -1px rgba(13, 148, 136, 0.1)',
                      marginBottom: '20px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <div style={{
                          background: '#3b5bfd',
                          color: '#ffffff',
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Sparkles size={18} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#1d4ed8' }}>
                          Join our Bench and get placed faster.
                        </h3>
                      </div>

                      <p style={{ fontSize: '13.5px', color: '#334155', lineHeight: '1.5', margin: '0 0 18px' }}>
                        Join our active bench and get fast-tracked to upcoming client projects. Your details are already filled in, so it takes one click.
                      </p>

                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={handleRedirectToJoinBench}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            background: 'linear-gradient(135deg, #3b5bfd 0%, #1d4ed8 100%)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '10px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(13, 148, 136, 0.25)'
                          }}
                        >
                          <Layers size={16} />
                          <span>Join the Bench</span>
                          <ArrowRight size={16} />
                        </button>

                        <button
                          type="button"
                          onClick={onClose}
                          style={{
                            background: '#ffffff',
                            color: '#475569',
                            border: '1px solid #cbd5e1',
                            borderRadius: '8px',
                            padding: '10px 18px',
                            fontSize: '13.5px',
                            fontWeight: '500',
                            cursor: 'pointer'
                          }}
                        >
                          No thanks, Close
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                <div className="jp-form-grid">
                  <div className="jp-form-field">
                    <label className="jp-form-label">First Name *</label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                      className="jp-form-input"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="jp-form-field">
                    <label className="jp-form-label">Last Name *</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                      className="jp-form-input"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="jp-form-grid">
                  <div className="jp-form-field">
                    <label className="jp-form-label">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email"
                      className="jp-form-input"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div className="jp-form-field">
                    <label className="jp-form-label">Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                      className="jp-form-input"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="jp-form-field jp-form-field-full">
                  <label className="jp-form-label">Position *</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    disabled={true}
                    className="jp-form-input jp-form-input-disabled"
                  />
                </div>

                <div className="jp-form-grid">
                  <div className="jp-form-field">
                    <label className="jp-form-label">Visa Type</label>
                    <select
                      name="visa_type"
                      value={formData.visa_type}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="jp-form-select"
                    >
                      <option value="">Select Visa Type</option>
                      {VISA_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="jp-form-field">
                    <label className="jp-form-label">Country</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="jp-form-select"
                    >
                      <option value="">Select Country</option>
                      {countriesData.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="jp-form-grid">
                  <div className="jp-form-field">
                    <label className="jp-form-label">State</label>
                    <select
                      name="current_state"
                      value={formData.current_state}
                      onChange={handleInputChange}
                      disabled={loading || !formData.country}
                      className="jp-form-select"
                    >
                      <option value="">Select State</option>
                      {statesList.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div className="jp-form-field">
                    <label className="jp-form-label">City</label>
                    <input
                      type="text"
                      name="current_city"
                      value={formData.current_city}
                      onChange={handleInputChange}
                      placeholder="Enter city"
                      className="jp-form-input"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="jp-form-field jp-form-field-full">
                  <label className="jp-form-label">Employment Type</label>
                  <div className="jp-form-checkbox-group">
                    {['W2', 'C2C'].map(type => (
                      <label key={type} className="jp-form-checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.employment_type.includes(type)}
                          onChange={() => handleEmploymentTypeChange(type)}
                          disabled={loading}
                          className="jp-form-checkbox"
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="jp-form-field jp-form-field-full">
                  <label className="jp-form-label">Upload Resume *</label>
                  <div
                    className={`jp-file-drop-zone ${dragActive ? 'jp-file-drop-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      id="resume-upload"
                      onChange={handleFileInputChange}
                      accept=".pdf,.doc,.docx"
                      required
                      disabled={loading}
                      className="jp-file-input-hidden"
                    />
                    <label htmlFor="resume-upload" className="jp-file-label">
                      {resumeFile ? (
                        <>
                          <CheckCircle size={28} className="jp-file-icon" />
                          <div className="jp-file-success-text">
                            Selected: {resumeFile.name}
                          </div>
                        </>
                      ) : (
                        <>
                          <Upload size={28} className="jp-file-icon" />
                          <div className="jp-file-text">
                            {dragActive ? 'Drop your resume here' : 'Click to upload or drag and drop'}
                          </div>
                          <div className="jp-file-hint">
                            PDF, DOC, or DOCX (max 5MB)
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  className="jp-submit-button"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  )}
</>
  );
};

// Location Tooltip Modal
const LocationTooltipModal = ({ isOpen, onClose, locations }) => {
  if (!isOpen || !locations || locations.length === 0) return null;

  return (
    <div 
      className="jp-location-tooltip-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999
      }}
    >
      <style>{`
        .jp-location-tooltip-box {
          visibility: visible;
          position: absolute;
          bottom: 120%;
          left: 0;
          background-color: #2d3748;
          color: white;
          padding: 10px 12px;
          border-radius: 6px;
          font-size: 0.85rem;
          white-space: normal;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 9999;
          line-height: 1.5;
          font-weight: 500;
          width: auto;
          max-width: 350px;
          pointer-events: auto;
        }

        .jp-location-tooltip-box::after {
          content: '';
          position: absolute;
          bottom: -6px;
          left: 10px;
          width: 0;
          height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-top: 6px solid #2d3748;
        }

        .jp-location-tooltip-title {
          margin: 0 0 8px 0;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .jp-location-tooltip-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .jp-location-tooltip-item {
          font-size: 0.85rem;
          line-height: 1.4;
          padding: 2px 0;
        }
      `}</style>
      
      <div 
        className="jp-location-tooltip-box"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="jp-location-tooltip-title">All Locations:</div>
        <div className="jp-location-tooltip-list">
          {locations.map((loc, idx) => (
            <div key={idx} className="jp-location-tooltip-item">
              {loc}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Multiselect Filter Component
const MultiSelectFilter = ({ icon: Icon, label, items, selected, onToggle, onClear, placeholder = "Select items" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const wrapperRef = React.useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Ensure items are unique before filtering
  const uniqueItems = [...new Set(items)];
  
  const filteredItems = uniqueItems.filter(item =>
    item.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="jp-multiselect-container" ref={wrapperRef}>
      <label className="jp-filter-label" style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        {Icon && <Icon size={16} color="#2563eb" />}
        <span>{label}</span>
      </label>
      <div className="jp-multiselect-wrapper">
        <div
          className="jp-multiselect-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>
            {selected.length === 0
              ? placeholder
              : `${selected.length} Selected`}
          </span>
          <ChevronDown
            size={20}
            className={`jp-chevron-icon ${isOpen ? 'jp-chevron-open' : ''}`}
          />
        </div>

        {isOpen && (
          <div className="jp-multiselect-menu">
            <div className="jp-multiselect-search">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="jp-multiselect-search-input"
              />
            </div>
            <div className="jp-multiselect-options">
              <div
                className="jp-multiselect-option"
                onClick={() => {
                  onClear();
                  setSearchQuery('');
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <RotateCcw size={12} color="#2563eb" />
                <span>Clear All</span>
              </div>
              {filteredItems.map(item => (
                <label key={item} className="jp-multiselect-option jp-checkbox-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(item)}
                    onChange={() => onToggle(item)}
                    className="jp-multiselect-checkbox"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



// Canonical Work Mode normalizer (Strictly 3 options: Remote, Hybrid, On-site)
const normalizeWorkMode = (wm) => {
  if (!wm) return 'On-site';
  const clean = wm.toString().trim().toLowerCase().replace(/[^a-z]/g, '');
  if (clean === 'remote') return 'Remote';
  if (clean === 'hybrid') return 'Hybrid';
  return 'On-site';
};

// Main Component
export default function JobOpenPortal() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [hoveredLocationRole, setHoveredLocationRole] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showLocationTooltip, setShowLocationTooltip] = useState(false);
  const [tooltipLocations, setTooltipLocations] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [viewMode, setViewMode] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');
  const [showBenchCallout, setShowBenchCallout] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Persistent filters — restored from sessionStorage on back-navigation
  const { filters, setFilters, handleClearFilter } = usePersistentFilters(
    'careersPageFilters',
    {
      keywordSearch: '',
      keywordTags: [],
      selectedLocations: [],
      selectedCountries: [],
      selectedExperience: [],
      selectedWorkModes: [],
    }
  );

  const { keywordSearch, keywordTags, selectedLocations, selectedCountries, selectedExperience, selectedWorkModes } = filters;

  const setKeywordSearch   = (v) => setFilters(f => ({ ...f, keywordSearch: typeof v === 'function' ? v(f.keywordSearch) : v }));
  const setKeywordTags     = (v) => setFilters(f => ({ ...f, keywordTags: typeof v === 'function' ? v(f.keywordTags) : v }));
  const setSelectedLocations  = (v) => setFilters(f => ({ ...f, selectedLocations: typeof v === 'function' ? v(f.selectedLocations) : v }));
  const setSelectedCountries  = (v) => setFilters(f => ({ ...f, selectedCountries: typeof v === 'function' ? v(f.selectedCountries) : v }));
  const setSelectedExperience = (v) => setFilters(f => ({ ...f, selectedExperience: typeof v === 'function' ? v(f.selectedExperience) : v }));
  const setSelectedWorkModes  = (v) => setFilters(f => ({ ...f, selectedWorkModes: typeof v === 'function' ? v(f.selectedWorkModes) : v }));

  const heroCanvasRef = useRef(null);

  useEffect(() => {
    fetchRoles();
  }, []);

  // Shimmering laser starburst and cyber matrix pixels (complements uploaded hero background)
  useEffect(() => {
    const canvas = heroCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const setCanvasSize = () => {
      if (!canvas) return;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // 1. Light floating stardust motes (soft glowing light specks)
    const moteCount = 36;
    const motes = [];
    for (let i = 0; i < moteCount; i++) {
      motes.push({
        x: Math.random() * (canvas.width || 1400),
        y: Math.random() * (canvas.height || 360),
        radius: Math.random() * 2 + 1,
        colorPrefix: i % 3 === 0 ? 'rgba(165, 243, 252, ' : (i % 3 === 1 ? 'rgba(56, 189, 248, ' : 'rgba(255, 255, 255, '),
        baseAlpha: Math.random() * 0.4 + 0.2,
        speedY: -0.2 - Math.random() * 0.35,
        speedX: (Math.random() - 0.5) * 0.25,
        phase: Math.random() * Math.PI * 2
      });
    }

    // 2. Light crosshair laser starburst flares matching cyber aesthetic
    const flareCount = 12;
    const flares = [];
    for (let i = 0; i < flareCount; i++) {
      flares.push({
        x: ((canvas.width || 1400) * 0.2) + Math.random() * ((canvas.width || 1400) * 0.75),
        y: Math.random() * (canvas.height || 360),
        size: Math.random() * 16 + 10,
        phase: Math.random() * Math.PI * 2,
        speed: 0.016 + Math.random() * 0.02
      });
    }

    // 3. Light horizontal cyber data streaks (gentle flowing light pulses)
    const streakCount = 5;
    const streaks = [];
    for (let i = 0; i < streakCount; i++) {
      streaks.push({
        x: Math.random() * (canvas.width || 1400),
        y: 40 + Math.random() * ((canvas.height || 360) - 80),
        length: 80 + Math.random() * 120,
        speed: 1.0 + Math.random() * 1.5,
        alpha: 0.15 + Math.random() * 0.2
      });
    }

    let time = 0;

    const render = () => {
      time += 0.016;
      const w = canvas.width;
      const h = canvas.height;
      if (w === 0 || h === 0) return;
      ctx.clearRect(0, 0, w, h);

      // Render light cyber streaks
      for (let i = 0; i < streaks.length; i++) {
        const s = streaks[i];
        s.x += s.speed;
        if (s.x > w + s.length) {
          s.x = -s.length;
          s.y = 40 + Math.random() * (h - 80);
        }

        const gradStreak = ctx.createLinearGradient(s.x, s.y, s.x + s.length, s.y);
        gradStreak.addColorStop(0, 'rgba(56, 189, 248, 0)');
        gradStreak.addColorStop(0.7, `rgba(165, 243, 252, ${s.alpha})`);
        gradStreak.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.strokeStyle = gradStreak;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x + s.length, s.y);
        ctx.stroke();
      }

      // Render light floating stardust motes
      for (let i = 0; i < motes.length; i++) {
        const m = motes[i];
        m.y += m.speedY;
        m.x += m.speedX;
        if (m.y < 0) {
          m.y = h;
          m.x = Math.random() * w;
        }
        if (m.x < 0) m.x = w;
        if (m.x > w) m.x = 0;

        const pulse = (Math.sin(time * 2 + m.phase) + 1) * 0.5;
        const alpha = m.baseAlpha * (0.6 + pulse * 0.4);

        // Soft light glow halo
        const haloGrad = ctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, m.radius * 3);
        haloGrad.addColorStop(0, `${m.colorPrefix}${alpha})`);
        haloGrad.addColorStop(1, `${m.colorPrefix}0)`);
        ctx.fillStyle = haloGrad;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.radius * 3, 0, Math.PI * 2);
        ctx.fill();

        // Crisp light center
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.9})`;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.radius * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }

      // Render light crosshair starburst flares
      for (let i = 0; i < flares.length; i++) {
        const flare = flares[i];
        const pulse = (Math.sin(time * 1.5 + flare.phase) + 1) * 0.5;
        const currentAlpha = 0.08 + pulse * 0.35;
        const armLength = flare.size * (0.6 + pulse * 0.5);

        ctx.save();
        ctx.translate(flare.x, flare.y);

        // Horizontal ray
        const gradH = ctx.createLinearGradient(-armLength, 0, armLength, 0);
        gradH.addColorStop(0, 'rgba(56, 189, 248, 0)');
        gradH.addColorStop(0.5, `rgba(165, 243, 252, ${currentAlpha})`);
        gradH.addColorStop(1, 'rgba(56, 189, 248, 0)');
        ctx.fillStyle = gradH;
        ctx.fillRect(-armLength, -1, armLength * 2, 2);

        // Vertical ray
        const gradV = ctx.createLinearGradient(0, -armLength * 0.8, 0, armLength * 0.8);
        gradV.addColorStop(0, 'rgba(45, 212, 191, 0)');
        gradV.addColorStop(0.5, `rgba(45, 212, 191, ${currentAlpha * 0.85})`);
        gradV.addColorStop(1, 'rgba(45, 212, 191, 0)');
        ctx.fillStyle = gradV;
        ctx.fillRect(-1, -armLength * 0.8, 2, armLength * 1.6);

        // Center light node
        ctx.beginPath();
        ctx.arc(0, 0, 1.5 + pulse * 1.0, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${0.4 + pulse * 0.5})`;
        ctx.shadowColor = '#a5f3fc';
        ctx.shadowBlur = 8;
        ctx.fill();

        ctx.restore();
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_URL}/api/recruitment/public/roles`);
      let rolesArray = response.data || [];
      
      if (Array.isArray(rolesArray)) {
        rolesArray = rolesArray
          .filter(role => role.status === 'Active' || role.status === 'Modified' || role.status === 'OPEN')
          .map(role => ({
            ...role,
            roleLocation: normalizeWorkMode(role.roleLocation),
            country: normalizeCountry(role.country)
          }));
      }
      
      setRoles(rolesArray);
      setCurrentPage(1);
      setError('');
    } catch (err) {
      console.error('Error fetching roles:', err.response?.data || err.message);
      setError('Failed to load job openings. Please try again later.');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const isUserAuthenticated = () => {
    const token = localStorage.getItem('token');
    return !!token;
  };

  const handleApplyClick = (role) => {
    const isAuth = isUserAuthenticated();
    setSelectedRole(role);
    
    if (!isAuth) {
      localStorage.removeItem('pendingJobApplication');
      const actualJobId = role?.jobId || role?.job_id || role?.id;
      const jobData = { id: actualJobId, role: role?.role };
      localStorage.setItem('pendingJobApplication', JSON.stringify(jobData));
      setShowAuthModal(true);
      return;
    } else {
      setShowForm(true);
    }
  };

  const handleJobTitleClick = (role) => {
    navigate(`/job/${role.id}`, { state: { role } });
  };

  const handleLoginRedirect = () => {
    window.location.href = '/login';
  };

  const handleSignupRedirect = () => {
    window.location.href = '/signup';
  };

  const getAllLocations = () => {
    const locations = new Set();
    roles.forEach(role => {
      // If countries are selected, only include locations from roles that match the selected countries
      if (selectedCountries.length > 0 && !selectedCountries.some(sc => normalizeCountry(sc) === normalizeCountry(role.country))) {
        return;
      }

      try {
        const locs = formatLocations(role);
        if (Array.isArray(locs)) {
          locs.forEach(loc => {
            if (loc && loc !== 'N/A' && loc !== 'Not specified' && loc !== 'Remote') {
              locations.add(loc);
            }
          });
        }
      } catch (error) {
        console.error('Error getting locations for role:', error);
      }
    });
    
    // Convert Set to Array and sort alphabetically
    return Array.from(locations).sort((a, b) => a.localeCompare(b));
  };

  const getAllCountries = () => {
    const countries = new Set();
    roles.forEach(role => {
      const c = normalizeCountry(role.country);
      if (c) countries.add(c);
    });
    return Array.from(countries).sort((a, b) => a.localeCompare(b));
  };

  const getAllExperience = () => {
    const experiences = new Set();
    roles.forEach(role => {
      if (role.experience) experiences.add(role.experience);
    });
    return Array.from(experiences).sort();
  };

  const getAllWorkModes = () => ['Remote', 'Hybrid', 'On-site'];

  const handleLocationToggle = (location) => {
    setSelectedLocations(prev => 
      prev.includes(location) 
        ? prev.filter(loc => loc !== location)
        : [...prev, location]
    );
    setCurrentPage(1);
  };

  const handleCountryToggle = (country) => {
    const canonical = normalizeCountry(country);
    setSelectedCountries(prev =>
      prev.some(c => normalizeCountry(c) === canonical)
        ? prev.filter(c => normalizeCountry(c) !== canonical)
        : [...prev, canonical]
    );
    setCurrentPage(1);
  };

  const handleExperienceToggle = (exp) => {
    setSelectedExperience(prev =>
      prev.includes(exp)
        ? prev.filter(e => e !== exp)
        : [...prev, exp]
    );
    setCurrentPage(1);
  };

  const handleWorkModeToggle = (mode) => {
    const canonical = normalizeWorkMode(mode);
    setSelectedWorkModes(prev =>
      prev.some(m => normalizeWorkMode(m) === canonical)
        ? prev.filter(m => normalizeWorkMode(m) !== canonical)
        : [...prev, canonical]
    );
    setCurrentPage(1);
  };

  const handleKeywordSearch = (e) => {
    if (e.key === 'Enter' && keywordSearch.trim()) {
      const keyword = keywordSearch.trim();
      if (!keywordTags.includes(keyword)) {
        setKeywordTags(prev => [...prev, keyword]);
      }
      setKeywordSearch('');
      setCurrentPage(1);
    }
  };

  const handleRemoveKeywordTag = (tag) => {
    setKeywordTags(prev => prev.filter(t => t !== tag));
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    handleClearFilter();
    setCurrentPage(1);
  };

  const filteredRoles = roles.filter(role => {
    const matchesKeyword = keywordTags.length === 0 || keywordTags.some(keyword => {
      const searchTerm = keyword.toLowerCase();
      const roleTitle = (role.role || '').toLowerCase();
      const roleType = (role.roleType || '').toLowerCase();
      const experience = (role.experience || '').toLowerCase();
      const workMode = (role.roleLocation || '').toLowerCase();
      const locations = formatLocations(role).join(' ').toLowerCase();
      const description = (role.description || role.jobDescription || '').toLowerCase();
      
      return roleTitle.includes(searchTerm) ||
             roleType.includes(searchTerm) ||
             experience.includes(searchTerm) ||
             workMode.includes(searchTerm) ||
             locations.includes(searchTerm) ||
             description.includes(searchTerm);
    });
    
    const matchesLocation = selectedLocations.length === 0 || 
      formatLocations(role).some(loc => {
        if (selectedLocations.includes(loc)) return true;
        const canonical = canonicalizeLocation(loc);
        return canonical && selectedLocations.includes(canonical);
      });
    
    const matchesCountry = selectedCountries.length === 0 || 
      selectedCountries.some(sc => normalizeCountry(sc) === normalizeCountry(role.country));
    
    const matchesExperience = selectedExperience.length === 0 || 
      (role.experience && selectedExperience.includes(role.experience));
    
    const matchesWorkMode = selectedWorkModes.length === 0 || 
      selectedWorkModes.some(selected => normalizeWorkMode(selected) === normalizeWorkMode(role.roleLocation));
    
    return matchesKeyword && matchesLocation && matchesCountry && matchesExperience && matchesWorkMode;
  });

  const totalRoles = filteredRoles.length;
  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRoles = filteredRoles.slice(startIndex, startIndex + itemsPerPage);

  const PaginationButton = ({ page, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`jp-page-button ${isActive ? 'jp-page-button-active' : ''}`}
    >
      {page}
    </button>
  );

  const hasActiveFilters = keywordTags.length > 0 || selectedLocations.length > 0 || 
                          selectedCountries.length > 0 || selectedExperience.length > 0 || 
                          selectedWorkModes.length > 0;

  const handleFindOpportunities = (e) => {
    if (e) e.preventDefault();
    if (keywordSearch.trim()) {
      const keyword = keywordSearch.trim();
      if (!keywordTags.includes(keyword)) {
        setKeywordTags(prev => [...prev, keyword]);
      }
      setKeywordSearch('');
      setCurrentPage(1);
    }
  };

  const remoteCount = roles.filter(r => normalizeWorkMode(r.roleLocation) === 'Remote').length;
  const hybridCount = roles.filter(r => normalizeWorkMode(r.roleLocation) === 'Hybrid').length;
  const onsiteCount = roles.filter(r => normalizeWorkMode(r.roleLocation) === 'On-site').length;

  return (
    <div className="jp-main-container">
      {/* STICKY TOP NAVIGATION BAR */}
      <nav className={`jp-sticky-navbar ${isScrolled ? 'jp-navbar-scrolled' : ''}`}>
        <div className="jp-navbar-inner">
          <a
            href="https://prophecytechs.com/"
            className="jp-header-logo-group"
            onClick={(e) => {
              if (!e.ctrlKey && !e.metaKey) {
                e.preventDefault();
                window.location.href = 'https://prophecytechs.com/';
              }
            }}
            title="Prophecy Technologies"
          >
            <img src={prophecyLogo2} alt="Prophecy Logo Icon" className="jp-header-logo-icon" />
            <img src={prophecyLogo} alt="Prophecy Technologies" className="jp-header-logo-text" />
          </a>

          <div className="jp-header-actions">
            <button
              type="button"
              className="jp-nav-bench-btn"
              onClick={() => navigate('/join-our-bench')}
            >
              <Layers size={16} />
              <span>Join our Bench</span>
            </button>
            <button
              type="button"
              className="jp-nav-login-btn"
              onClick={() => navigate('/login')}
            >
              <LogIn size={16} />
              <span>Candidate Login</span>
            </button>
          </div>
        </div>
      </nav>

      {/* SECTION 1: ANIMATED CYBER HERO WITH EXACT USER BACKGROUND */}
      <section className="jp-hero-wrapper">
        <div
          className="jp-hero-bg-layer"
          style={{ backgroundImage: `url(${careersHeroBg})` }}
        />
        <canvas ref={heroCanvasRef} className="jp-hero-canvas" />
        <div className="jp-hero-container">
          <div className="jp-hero-grid">
            <div className="jp-hero-intro">
              <div className="jp-hero-eyebrow">
                <Globe size={14} className="jp-hero-eyebrow-icon" />
                <span>Global Talent Platform</span>
                <span className="jp-hero-eyebrow-slash">/</span>
                <span className="jp-hero-eyebrow-accent">Engineering & AI Careers</span>
              </div>
              <h1 className="jp-hero-title">
                <span className="jp-hero-title-white">Great talent.</span>{' '}
                <span className="jp-word-gradient-cyan-teal">Extraordinary possibilities.</span>
              </h1>
              <p className="jp-hero-lede">
                Find your place at Prophecy. Explore opportunities to build, solve, and grow with teams shaping what comes next.
              </p>
            </div>

            <aside className="jp-hero-aside-celestial">
              <div className="jp-hero-stat-block">
                <div className="jp-hero-stat-number">{roles.length || 0}</div>
                <div className="jp-hero-stat-label">roles on our careers portal</div>
                <button
                  type="button"
                  onClick={() => {
                    const el = document.querySelector('.jp-workspace-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="jp-hero-stat-link"
                >
                  <span>Explore all opportunities</span>
                  <span className="jp-hero-stat-arrow">↗</span>
                </button>
                <div className="jp-hero-stat-sub">
                  Actively hiring globally across tech & AI
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* SECTION 2: WORKSPACE (Search bar + 2-Column Sidebar & Results with Minimized Gaps) */}
      <section className="jp-workspace-section">
        <div className="jp-workspace-container">
          {/* Full-width Compact Search Bar */}
          <form className="jp-searchbar-full" onSubmit={handleFindOpportunities}>
            <Search size={18} className="jp-searchbar-icon" />
            <input
              type="text"
              placeholder="Search by skills, location, role, experience... (Press Enter to add)"
              value={keywordSearch}
              onChange={(e) => setKeywordSearch(e.target.value)}
              onKeyDown={handleKeywordSearch}
              className="jp-searchbar-input"
            />
            {keywordSearch && (
              <button
                type="button"
                onClick={() => setKeywordSearch('')}
                className="jp-searchbar-clear-btn"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
            <button type="submit" className="jp-searchbar-submit-btn">
              <span>Find opportunities</span>
              <ArrowRight size={14} />
            </button>
          </form>

          {/* 2-Column Workspace Layout */}
          <div className={`jp-job-layout ${!isSidebarOpen ? 'sidebar-closed' : ''}`}>
            {/* Left Column: Filter Sidebar */}
            <aside className={`jp-filter-sidebar ${!isSidebarOpen ? 'jp-sidebar-collapsed' : ''}`}>
              {!isSidebarOpen ? (
                <div className="jp-sidebar-collapsed-strip">
                  <button
                    type="button"
                    onClick={() => setIsSidebarOpen(true)}
                    className="jp-sidebar-collapsed-btn"
                    title="Open filter opportunities"
                    aria-label="Open filter opportunities"
                  >
                    <Menu size={18} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="jp-filter-sidebar-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <SlidersHorizontal size={15} color="#2563eb" />
                      <h3 className="jp-filter-sidebar-title">
                        Filter <span className="jp-sidebar-accent-text">opportunities</span>
                      </h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={handleClearAllFilters}
                          className="jp-filter-reset-btn"
                          title="Reset all filters"
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            <RotateCcw size={12} />
                            Reset
                          </span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsSidebarOpen(false)}
                        className="jp-sidebar-header-toggle-btn"
                        title="Close filter sidebar"
                        aria-label="Close filter sidebar"
                      >
                        <Menu size={18} />
                      </button>
                    </div>
                  </div>

              {/* 1. Country Dropdown */}
              <div className="jp-filter-field">
                <MultiSelectFilter
                  icon={Globe}
                  label="Country"
                  items={getAllCountries()}
                  selected={selectedCountries}
                  onToggle={handleCountryToggle}
                  onClear={() => {
                    setSelectedCountries([]);
                    setCurrentPage(1);
                  }}
                  placeholder="Select Country"
                />
              </div>

              {/* 2. Location Dropdown */}
              <div className="jp-filter-field">
                <MultiSelectFilter
                  icon={MapPin}
                  label="Location"
                  items={getAllLocations()}
                  selected={selectedLocations}
                  onToggle={handleLocationToggle}
                  onClear={() => {
                    setSelectedLocations([]);
                    setCurrentPage(1);
                  }}
                  placeholder="Select Location"
                />
              </div>

              {/* 3. Work Mode - Checkboxes with Counts (Strictly 3 options: Remote, Hybrid, On-site) */}
              <fieldset className="jp-workmode-fieldset">
                <legend className="jp-workmode-legend" style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <Laptop size={16} color="#2563eb" />
                  <span>Work mode</span>
                </legend>
                {[
                  { label: 'Remote', count: remoteCount, icon: Laptop, color: '#3b5bfd' },
                  { label: 'Hybrid', count: hybridCount, icon: Layers, color: '#4f46e5' },
                  { label: 'On-site', count: onsiteCount, icon: Building2, color: '#ea580c' }
                ].map((mode) => {
                  const isChecked = selectedWorkModes.some(m => normalizeWorkMode(m) === mode.label);
                  const ModeIcon = mode.icon;

                  return (
                    <label key={mode.label} className="jp-workmode-item">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleWorkModeToggle(mode.label)}
                        className="jp-workmode-checkbox"
                      />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <ModeIcon size={12} color={mode.color} />
                        {mode.label}
                      </span>
                      <span className="jp-workmode-count">{mode.count}</span>
                    </label>
                  );
                })}
              </fieldset>

              {/* 4. Experience Dropdown */}
              <div className="jp-filter-field">
                <MultiSelectFilter
                  icon={Zap}
                  label="Experience"
                  items={getAllExperience()}
                  selected={selectedExperience}
                  onToggle={handleExperienceToggle}
                  onClear={() => {
                    setSelectedExperience([]);
                    setCurrentPage(1);
                  }}
                  placeholder="Select Experience"
                />
              </div>

              {/* Join our Bench Callout Widget */}
              {showBenchCallout && (
                <div className="jp-bench-callout-card">
                  <div className="jp-bench-callout-header">
                    <div className="jp-bench-callout-icon">
                      <Sparkles size={16} color="#0284c7" />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowBenchCallout(false)}
                      className="jp-bench-callout-close-btn"
                      title="Close"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <h4 className="jp-bench-callout-title">Join our Bench and get placed faster.</h4>
                  <p className="jp-bench-callout-desc">
                    Join our active bench and get fast-tracked to upcoming client projects. Your details are already filled in, so it takes one click.
                  </p>
                  <div className="jp-bench-callout-actions">
                    <button
                      type="button"
                      onClick={() => navigate('/join-our-bench')}
                      className="jp-bench-callout-primary-btn"
                    >
                      <span>Join the Bench</span>
                      <ArrowRight size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBenchCallout(false)}
                      className="jp-bench-callout-secondary-btn"
                    >
                      No thanks, Close
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </aside>

            {/* Right Column: Results Area */}
            <main className="jp-results-area">
              {/* Results Top Header */}
              <div className="jp-results-top-bar">
                <div>
                  <h2 className="jp-results-top-title">
                    Find your next <span className="jp-colorful-accent-text">opportunity</span>
                  </h2>
                  <p className="jp-results-top-count">
                    Showing <span className="jp-count-badge">{filteredRoles.length}</span> open position{filteredRoles.length === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="jp-views-toggle-group">
                  <button 
                    type="button"
                    onClick={() => setViewMode('card')} 
                    className={`jp-view-toggle-btn ${viewMode === 'card' ? 'jp-view-selected' : ''}`}
                    title="Grid View"
                  >
                    <LayoutGrid size={17} />
                  </button>
                  <button 
                    type="button"
                    onClick={() => setViewMode('table')} 
                    className={`jp-view-toggle-btn ${viewMode === 'table' ? 'jp-view-selected' : ''}`}
                    title="Table View"
                  >
                    <List size={17} />
                  </button>
                </div>
              </div>

              {/* Active Filter Chips */}
              {hasActiveFilters && (
                <div className="jp-active-tags-row">
                  <span className="jp-active-tags-label">Active filters:</span>
                  {keywordTags.map((tag, index) => (
                    <span key={`kw-${index}`} className="jp-active-tag-chip">
                      {tag}
                      <button type="button" onClick={() => handleRemoveKeywordTag(tag)} className="jp-active-tag-close">×</button>
                    </span>
                  ))}
                  {selectedCountries.map((c, index) => (
                    <span key={`c-${index}`} className="jp-active-tag-chip">
                      {normalizeCountry(c)}
                      <button type="button" onClick={() => handleCountryToggle(c)} className="jp-active-tag-close">×</button>
                    </span>
                  ))}
                  {selectedLocations.map((l, index) => (
                    <span key={`l-${index}`} className="jp-active-tag-chip">
                      {l}
                      <button type="button" onClick={() => handleLocationToggle(l)} className="jp-active-tag-close">×</button>
                    </span>
                  ))}
                  {selectedWorkModes.map((w, index) => (
                    <span key={`w-${index}`} className="jp-active-tag-chip">
                      {w}
                      <button type="button" onClick={() => handleWorkModeToggle(w)} className="jp-active-tag-close">×</button>
                    </span>
                  ))}
                  {selectedExperience.map((e, index) => (
                    <span key={`e-${index}`} className="jp-active-tag-chip">
                      {e}
                      <button type="button" onClick={() => handleExperienceToggle(e)} className="jp-active-tag-close">×</button>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={handleClearAllFilters}
                    className="jp-active-clear-all"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Job Results Content */}
              <div>
                {loading ? (
                  <div className="jp-loading-overlay">
                    <div className="jp-loading-spinner-wrap"></div>
                    <p className="jp-loading-text">Loading opportunities...</p>
                  </div>
                ) : error ? (
                  <div className="jp-error-message">{error}</div>
                ) : filteredRoles.length === 0 ? (
                  <div className="jp-empty-message">
                    No job openings match your search criteria. Try adjusting your filters.
                  </div>
                ) : (
                  <>
                    {viewMode === 'card' ? (
                      <div className="jp-card-grid">
                        {paginatedRoles.map((role) => {
                          const locations = formatLocations(role);
                          const workMode = role.roleLocation || 'On-site';
                          const workModeClass = (role.roleLocation || '').toLowerCase().includes('remote') 
                            ? 'jp-card-chip-remote' 
                            : (role.roleLocation || '').toLowerCase().includes('hybrid') 
                              ? 'jp-card-chip-hybrid' 
                              : 'jp-card-chip-onsite';
                          const roleTypeClass = (role.roleType || '').toLowerCase().includes('contract')
                            ? 'jp-tag-contract'
                            : (role.roleType || '').toLowerCase().includes('part')
                              ? 'jp-tag-parttime'
                              : 'jp-tag-fulltime';

                          return (
                            <div 
                              key={role.id} 
                              className="jp-job-card"
                              onClick={() => handleJobTitleClick(role)}
                            >
                              <div className="jp-card-top-row">
                                <span className={`jp-card-type-tag ${roleTypeClass}`}>
                                  {role.roleType || 'Full-time'}
                                </span>
                              </div>
                              <h3 className="jp-card-title">
                                {role.role}
                              </h3>
                              <p className="jp-card-location-text">
                                <MapPin size={15} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '5px', color: '#4f46e5', flexShrink: 0 }} />
                                <span>{locations.join(', ') || 'N/A'}</span>
                              </p>
                              <div className="jp-card-chips-row">
                                <span className={`jp-card-chip ${workModeClass}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                  {workMode.toLowerCase().includes('remote') ? (
                                    <Laptop size={13} />
                                  ) : workMode.toLowerCase().includes('hybrid') ? (
                                    <Layers size={13} />
                                  ) : (
                                    <Building2 size={13} />
                                  )}
                                  <span>{workMode}</span>
                                </span>
                                {role.experience && (
                                  <span className="jp-card-chip jp-card-chip-exp" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                    <Zap size={13} />
                                    <span>{role.experience}</span>
                                  </span>
                                )}
                              </div>

                              <div className="jp-card-bottom-row">
                                <span className="jp-card-date" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '13px', fontWeight: 500 }}>
                                  <Calendar size={14} color="#94a3b8" />
                                  <span>Posted: {formatDateDisplay(role.postedDate) || 'N/A'}</span>
                                </span>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApplyClick(role);
                                  }}
                                  className="jp-card-apply-action"
                                >
                                  <span>Apply</span>
                                  <ArrowRight size={15} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="jp-table-wrapper">
                        <table className="jp-table-main">
                          <colgroup>
                            <col className="jp-col-role" />
                            <col className="jp-col-type" />
                            <col className="jp-col-location" />
                            <col className="jp-col-workmode" />
                            <col className="jp-col-exp" />
                            <col className="jp-col-date" />
                            <col className="jp-col-action" />
                          </colgroup>
                          <thead className="jp-table-header">
                            <tr>
                              <th><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Briefcase size={14} color="#475569" />Role Title</span></th>
                              <th><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Layers size={14} color="#475569" />Role Type</span></th>
                              <th><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><MapPin size={14} color="#475569" />Location</span></th>
                              <th><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Laptop size={14} color="#475569" />Work Mode</span></th>
                              <th><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Zap size={14} color="#475569" />Experience</span></th>
                              <th><span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Calendar size={14} color="#475569" />Posted Date</span></th>
                              <th style={{ textAlign: 'right' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedRoles.map((role) => {
                              const locations = formatLocations(role);
                              const roleTypeClass = (role.roleType || '').toLowerCase().includes('contract')
                                ? 'jp-tag-contract'
                                : (role.roleType || '').toLowerCase().includes('part')
                                ? 'jp-tag-parttime'
                                : 'jp-tag-fulltime';

                              return (
                                <tr 
                                  key={role.id} 
                                  className="jp-table-row"
                                  onClick={() => handleJobTitleClick(role)}
                                >
                                  <td className="jp-table-cell">
                                    <span className="jp-table-role-link">
                                      {role.role}
                                    </span>
                                  </td>
                                  <td className="jp-table-cell">
                                    <span className={`jp-card-type-tag ${roleTypeClass}`}>{role.roleType || 'Full-time'}</span>
                                  </td>
                                  <td className="jp-table-cell" style={{ position: 'relative' }}>
                                    <div 
                                      className="jp-location-cell-wrapper"
                                      onMouseEnter={(e) => {
                                        if (locations.length > 1) {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setTooltipPosition({
                                            top: rect.top - 120,
                                            left: rect.left
                                          });
                                          setHoveredLocationRole(role.id);
                                        }
                                      }}
                                      onMouseLeave={() => setHoveredLocationRole(null)}
                                    >
                                      <span>{locations[0] || 'N/A'}</span>
                                      {locations.length > 1 && (
                                        <span style={{ marginLeft: '6px', color: '#2859df', fontWeight: 600, fontSize: '12px' }}>
                                          +{locations.length - 1} more
                                        </span>
                                      )}
                                      {locations.length > 1 && hoveredLocationRole === role.id && (
                                        <div 
                                          style={{
                                            position: 'fixed',
                                            top: tooltipPosition.top + 'px',
                                            left: tooltipPosition.left + 'px',
                                            backgroundColor: '#102747',
                                            color: 'white',
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            fontSize: '0.85rem',
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                                            zIndex: 10000,
                                            maxWidth: '320px',
                                            pointerEvents: 'none'
                                          }}
                                        >
                                          <strong style={{ display: 'block', marginBottom: '4px', color: '#9cbaff' }}>All Locations:</strong>
                                          {locations.map((loc, lIdx) => (
                                            <div key={lIdx} style={{ fontSize: '0.82rem', padding: '2px 0' }}>{loc}</div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="jp-table-cell">
                                    <span className={`jp-card-chip ${(role.roleLocation || '').toLowerCase().includes('remote') ? 'jp-card-chip-remote' : (role.roleLocation || '').toLowerCase().includes('hybrid') ? 'jp-card-chip-hybrid' : 'jp-card-chip-onsite'}`}>
                                      {role.roleLocation || 'On-site'}
                                    </span>
                                  </td>
                                  <td className="jp-table-cell">{role.experience || 'N/A'}</td>
                                  <td className="jp-table-cell">{formatDateDisplay(role.postedDate) || 'N/A'}</td>
                                  <td className="jp-table-cell" style={{ textAlign: 'right' }}>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleApplyClick(role);
                                      }}
                                      className="jp-table-apply-btn"
                                    >
                                      Apply
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="jp-pagination-wrapper">
                        <div className="jp-pagination-info-wrapper">
                          <div className="jp-show-per-page-selector">
                            <label className="jp-show-per-page-label">Show:</label>
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                              className="jp-show-per-page-dropdown"
                            >
                              <option value={10}>10</option>
                              <option value={15}>15</option>
                              <option value={20}>20</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                            <span className="jp-show-per-page-suffix">per page</span>
                          </div>

                          <div className="jp-pagination-controls">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="jp-page-button"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <ChevronLeft size={14} />
                              <span>Previous</span>
                            </button>

                            {totalPages <= 7 ? (
                              Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                <PaginationButton
                                  key={page}
                                  page={page}
                                  isActive={currentPage === page}
                                  onClick={() => setCurrentPage(page)}
                                />
                              ))
                            ) : (
                              <>
                                <PaginationButton
                                  page={1}
                                  isActive={currentPage === 1}
                                  onClick={() => setCurrentPage(1)}
                                />

                                {currentPage > 4 && (
                                  <span className="jp-page-ellipsis">...</span>
                                )}

                                {Array.from({ length: 5 }, (_, i) => {
                                  const page = currentPage - 2 + i;
                                  return page > 1 && page < totalPages ? (
                                    <PaginationButton
                                      key={page}
                                      page={page}
                                      isActive={currentPage === page}
                                      onClick={() => setCurrentPage(page)}
                                    />
                                  ) : null;
                                })}

                                {currentPage < totalPages - 3 && (
                                  <span className="jp-page-ellipsis">...</span>
                                )}

                                <PaginationButton
                                  page={totalPages}
                                  isActive={currentPage === totalPages}
                                  onClick={() => setCurrentPage(totalPages)}
                                />
                              </>
                            )}

                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="jp-page-button"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <span>Next</span>
                              <ChevronRight size={14} />
                            </button>
                          </div>

                          <div className="jp-results-summary">
                            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalRoles)} of {totalRoles} roles
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </main>
          </div>
        </div>
      </section>

      <LocationTooltipModal
        isOpen={showLocationTooltip}
        onClose={() => setShowLocationTooltip(false)}
        locations={tooltipLocations}
      />
      <AuthRequiredModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginClick={handleLoginRedirect}
        onGuestClick={() => {
          setShowAuthModal(false);
          setShowForm(true);
        }}
      />

      <CandidateFormPopup
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        selectedRole={selectedRole}
      />
    </div>
  );
}